import { UseQueryOptions, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { ArrayElement, NullableType } from '@/models'
import { keepPreviousDataWithPlaceholder } from '@/utils'

import { SelectOption, SelectOptionValue } from './select.types'

const defaultTransform = <T>(data: T): T => data

type RequestDataGuard = {
  search?: string
  limit?: number
  skip?: number
  // biome-ignore lint/suspicious/noExplicitAny: intersecting with `any` lets TRequest's extra request fields (e.g. `buildingId: number`) satisfy this constraint bidirectionally, so callers' `query` functions infer their own concrete request type instead of widening to this base shape
} & Record<string, any>

export type UseSelectOptionsQueryFunc<TRequest extends RequestDataGuard, TData> = (
  data: TRequest,
  signal?: AbortSignal,
) => Promise<TData>

export type UseSelectOptionsParams<
  TRequest extends RequestDataGuard,
  TData,
  // NOTE: Preserve `V` (SelectOptionValue) if `TData` looks similar to `SelectOption` (as `defaultTransform` above)
  V extends SelectOptionValue = ArrayElement<TData> extends { value: SelectOptionValue }
    ? ArrayElement<TData>['value']
    : SelectOptionValue,
  TOption extends SelectOption<V> = SelectOption<V>,
> = {
  data?: TData
  query?: UseSelectOptionsQueryFunc<TRequest, TData>
  requestData?:
    | Omit<TRequest, keyof RequestDataGuard>
    | ((search: string) => Omit<TRequest, keyof RequestDataGuard>)
  transform?: (data: NullableType<TData>) => TOption[]
}

// TODO: figure out how to seamlessly integrate `search` functionality with `useTreeOptions` hook,
// e.g., merge them together and make `useSelectOptions` support tree-like data structures
export const useSelectOptions = <
  TRequest extends RequestDataGuard,
  TData,
  V extends SelectOptionValue = ArrayElement<TData> extends { value: SelectOptionValue }
    ? ArrayElement<TData>['value']
    : SelectOptionValue,
  TOption extends SelectOption<V> = SelectOption<V>,
>(
  {
    data = [] as TData,
    query,
    requestData,
    transform = defaultTransform as (data: NullableType<TData>) => TOption[],
  }: UseSelectOptionsParams<TRequest, TData, V, TOption>,
  queryOptions: Omit<UseQueryOptions<TData>, 'queryFn' | 'placeholderData'>,
) => {
  const { queryKey = [], enabled: queryEnabled = true, ...restQueryOptions } = queryOptions

  const [search, setSearch] = useState('')

  const parsedRequestData = useMemo(() => {
    return typeof requestData === 'function' ? requestData(search) : requestData
  }, [search, requestData])

  const queryResult = useQuery<TData>({
    ...restQueryOptions,
    enabled: queryEnabled && !!query,
    placeholderData: keepPreviousDataWithPlaceholder(data),
    // NOTE: by default, async select will fetch 50 items only, consider making it configurable if needed
    queryFn: ({ signal }) => {
      if (!query) {
        // Note: guarded by `enabled: queryEnabled && !!query` above - `query` is always
        // defined by the time this runs.
        throw new Error('useSelectOptions: `query` is required when the async query runs')
      }
      return query({ ...parsedRequestData, limit: 50, search, skip: 0 } as TRequest, signal)
    },
    queryKey: ['useSelectOptions', search, ...queryKey],
  })

  const selectOptions = useMemo(() => {
    if (query) {
      const asyncData = queryResult.isPlaceholderData ? data : queryResult.data
      return transform(asyncData)
    }

    if (!data) return []

    const initialOptions = transform(data)

    const searchQuery = search.trim().toLowerCase()
    if (!searchQuery) return initialOptions
    return initialOptions.filter((option) => option.label.toLowerCase().includes(searchQuery))
  }, [data, search, transform, query, queryResult.isPlaceholderData, queryResult.data])

  return {
    ...queryResult,
    selectProps: {
      loading: queryResult.isFetching,
      onSearchChange: setSearch,
      options: selectOptions,
      search,
    },
  }
}
